import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { getErrorMessage } from '../services/api';
import Layout from '../components/Layout';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatIST = (utcDateString) => {
  const date = new Date(utcDateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const formatDuration = (min) => {
  if (!min) return '—';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  live:      { label: 'Live',      bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500'    },
  completed: { label: 'Completed', bg: 'bg-gray-100',   text: 'text-gray-600',    dot: 'bg-gray-400'    },
  cancelled: { label: 'Cancelled', bg: 'bg-red-50',     text: 'text-red-600',     dot: 'bg-red-400'     },
};

const getStatusConfig = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.scheduled;

// ── Countdown Hook ───────────────────────────────────────────────────────────

const useCountdown = (targetIso) => {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    if (!targetIso) return;
    const target = new Date(targetIso).getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) { setRemaining(''); return; }
      const days = Math.floor(diff / 86400000);
      const hrs  = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      if (days > 0)     setRemaining(`${days}d ${hrs}h`);
      else if (hrs > 0) setRemaining(`${hrs}h ${mins}m`);
      else              setRemaining(`${mins}m ${secs}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return remaining;
};

const CountdownBadge = ({ scheduledAt }) => {
  const remaining = useCountdown(scheduledAt);
  if (!remaining) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {remaining}
    </span>
  );
};

// ── Skeleton ─────────────────────────────────────────────────────────────────

const PageSkeleton = () => (
  <Layout>
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="skeleton h-8 w-48" />
        <div className="skeleton h-10 w-32 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="rounded-2xl p-5 skeleton h-20" />)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="h-2 skeleton" />
            <div className="p-5 space-y-3">
              <div className="skeleton h-5 w-3/5" />
              <div className="skeleton h-4 w-2/3" />
              <div className="flex gap-2 mt-4">
                <div className="skeleton h-9 w-full rounded-xl" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Layout>
);

// ── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const styles = { success: 'bg-emerald-50 border-emerald-200 text-emerald-800', error: 'bg-red-50 border-red-200 text-red-800' };
  const icons  = { success: 'M5 13l4 4L19 7', error: 'M6 18L18 6M6 6l12 12' };
  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${styles[type]}`}>
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[type]} />
      </svg>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ── Confirm Modal ────────────────────────────────────────────────────────────

const ConfirmModal = ({ title, message, confirmLabel, confirmColor, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
    <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 p-6 w-full max-w-sm animate-fade-in">
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 mb-6">{message}</p>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} disabled={loading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
        <button onClick={onConfirm} disabled={loading} className={`px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50 ${confirmColor}`}>
          {loading ? 'Processing...' : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ── Class Form Modal ─────────────────────────────────────────────────────────

const ClassFormModal = ({ cls, batches, trainers, defaultTrainerId, onSave, onCancel, loading }) => {
  const isEdit = !!cls;
  const [form, setForm] = useState({
    batch_id:        cls?.batch_id        || '',
    trainer_id:      cls?.trainer_id      || defaultTrainerId || '',
    title:           cls?.title           || '',
    description:     cls?.description     || '',
    scheduled_at:    toDatetimeLocal(cls?.scheduled_at) || '',
    duration_minutes: cls?.duration_minutes || 60,
    meeting_link:    cls?.meeting_link    || '',
  });
  const [formError, setFormError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim())       return setFormError('Class title is required.');
    if (!form.batch_id)           return setFormError('Please select a batch.');
    if (!form.trainer_id)         return setFormError('Please select a trainer.');
    if (!form.scheduled_at)       return setFormError('Scheduled date/time is required.');
    if (!form.duration_minutes || form.duration_minutes < 1) return setFormError('Duration must be at least 1 minute.');

    onSave({
      batch_id:         form.batch_id,
      trainer_id:       form.trainer_id,
      title:            form.title.trim(),
      description:      form.description.trim() || null,
      scheduled_at:     new Date(form.scheduled_at).toISOString(),
      duration_minutes: Number(form.duration_minutes),
      meeting_link:     form.meeting_link.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit Class' : 'Schedule New Class'}</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Class Title</label>
            <input ref={inputRef} type="text" name="title" value={form.title} onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="e.g. Introduction to React Hooks" required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm resize-none"
              placeholder="Brief overview of what will be covered..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Batch</label>
              <select name="batch_id" value={form.batch_id} onChange={handleChange} required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white">
                <option value="">Select batch</option>
                {batches.map((b) => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Trainer</label>
              <select name="trainer_id" value={form.trainer_id} onChange={handleChange} required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white">
                <option value="">Select trainer</option>
                {trainers.map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Scheduled At</label>
              <input type="datetime-local" name="scheduled_at" value={form.scheduled_at} onChange={handleChange} required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration (minutes)</label>
              <input type="number" name="duration_minutes" value={form.duration_minutes} onChange={handleChange} min="1" max="480" required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Meeting Link <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="url" name="meeting_link" value={form.meeting_link} onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="https://meet.google.com/... or Zoom / Teams link" />
            <p className="text-xs text-gray-400 mt-1">Paste any meeting URL — Google Meet, Zoom, Teams, etc.</p>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onCancel} disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
            <button type="submit" disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50">
              {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Schedule Class'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

const ClassManagement = () => {
  const { user } = useAuth();
  const isStudent = user?.role === 'student';
  const isAdminOrTrainer = user?.role === 'admin' || user?.role === 'trainer';

  const [classes, setClasses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const [showForm, setShowForm] = useState(false);
  const [editClass, setEditClass] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Attendance tracking: classId → 'marking' | 'marked' | null
  const [attendanceState, setAttendanceState] = useState({});
  // Attendance counts for live classes (trainer view): classId → count
  const [attendanceCounts, setAttendanceCounts] = useState({});

  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchClasses = useCallback(async () => {
    try {
      const res = await api.get('/api/classes');
      setClasses(res.data || []);
      setError('');
    } catch { setError('Failed to load classes.'); }
  }, []);

  const fetchBatches = useCallback(async () => {
    try { const res = await api.get('/api/batches'); setBatches(res.data || []); } catch {}
  }, []);

  const fetchTrainers = useCallback(async () => {
    if (user?.role === 'trainer') { setTrainers([{ id: user.id, full_name: user.full_name }]); return; }
    try {
      const res = await api.get('/api/users?role=trainer&per_page=100');
      const list = res.data.users || res.data || [];
      setTrainers(list.map((t) => ({ id: t.id, full_name: t.full_name })));
    } catch {}
  }, [user]);

  // Check which live classes the student has already marked
  const checkStudentAttendance = useCallback(async (classList) => {
    if (!isStudent) return;
    const liveClasses = classList.filter((c) => c.status === 'live');
    if (liveClasses.length === 0) return;

    const stateMap = {};
    await Promise.all(
      liveClasses.map(async (cls) => {
        try {
          const res = await api.get(`/api/attendance?class_id=${cls.id}`);
          const records = res.data || [];
          stateMap[cls.id] = records.length > 0 ? 'marked' : null;
        } catch {
          stateMap[cls.id] = null;
        }
      })
    );
    setAttendanceState((prev) => ({ ...prev, ...stateMap }));
  }, [isStudent]);

  // Fetch attendance counts for live classes (trainer/admin view)
  const fetchAttendanceCounts = useCallback(async (classList) => {
    if (!isAdminOrTrainer) return;
    const liveClasses = classList.filter((c) => c.status === 'live');
    if (liveClasses.length === 0) return;

    const counts = {};
    await Promise.all(
      liveClasses.map(async (cls) => {
        try {
          const res = await api.get(`/api/attendance/classes/${cls.id}/summary`);
          counts[cls.id] = res.data?.present || 0;
        } catch {
          counts[cls.id] = 0;
        }
      })
    );
    setAttendanceCounts((prev) => ({ ...prev, ...counts }));
  }, [isAdminOrTrainer]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const calls = [fetchClasses(), fetchBatches()];
      if (!isStudent) calls.push(fetchTrainers());
      await Promise.all(calls);
      setLoading(false);
    };
    load();
  }, [fetchClasses, fetchBatches, fetchTrainers, isStudent]);

  // After classes load, check attendance status for live classes
  useEffect(() => {
    if (classes.length > 0) {
      checkStudentAttendance(classes);
      fetchAttendanceCounts(classes);
    }
  }, [classes, checkStudentAttendance, fetchAttendanceCounts]);

  // Poll every 30 seconds to refresh class statuses
  useEffect(() => {
    const interval = setInterval(() => {
      fetchClasses();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchClasses]);

  const now = new Date();
  const totalClasses     = classes.length;
  const upcomingClasses  = classes.filter((c) => c.status === 'scheduled' && new Date(c.scheduled_at) > now).length;
  const completedClasses = classes.filter((c) => c.status === 'completed').length;
  const liveClasses      = classes.filter((c) => c.status === 'live').length;

  const filtered = classes
    .filter((c) => {
      if (c.status === 'cancelled') return false;
      if (statusFilter && c.status !== statusFilter) return false;
      if (batchFilter && String(c.batch_id) !== batchFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (c.title || '').toLowerCase().includes(q) ||
               (c.batch_name || '').toLowerCase().includes(q) ||
               (c.trainer_name || '').toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      const at = new Date(a.scheduled_at || 0).getTime();
      const bt = new Date(b.scheduled_at || 0).getTime();
      return sortDir === 'desc' ? bt - at : at - bt;
    });

  const handleCreate = async (form) => {
    setModalLoading(true);
    try {
      await api.post('/api/classes', form);
      setShowForm(false);
      showToast('Class scheduled successfully');
      fetchClasses();
    } catch (err) { showToast(getErrorMessage(err, 'Failed to create class'), 'error'); }
    finally { setModalLoading(false); }
  };

  const handleEdit = async (form) => {
    setModalLoading(true);
    try {
      await api.put(`/api/classes/${editClass.id}`, form);
      setEditClass(null);
      showToast('Class updated successfully');
      fetchClasses();
    } catch (err) { showToast(getErrorMessage(err, 'Failed to update class'), 'error'); }
    finally { setModalLoading(false); }
  };

  const handleDelete = async () => {
    setModalLoading(true);
    try {
      await api.delete(`/api/classes/${deleteTarget.id}`);
      setDeleteTarget(null);
      showToast('Class deleted successfully');
      fetchClasses();
    } catch (err) { showToast(getErrorMessage(err, 'Failed to delete class'), 'error'); }
    finally { setModalLoading(false); }
  };

  const handleStartClass = async (cls) => {
    try {
      await api.post(`/api/classes/${cls.id}/start`);
      showToast(`"${cls.title}" is now live`);
      fetchClasses();
    } catch (err) { showToast(getErrorMessage(err, 'Failed to start class'), 'error'); }
  };

  const handleEndClass = async (cls) => {
    try {
      await api.post(`/api/classes/${cls.id}/end`);
      showToast(`"${cls.title}" has ended`);
      fetchClasses();
    } catch (err) { showToast(getErrorMessage(err, 'Failed to end class'), 'error'); }
  };

  const handleMarkPresent = async (cls) => {
    setAttendanceState((prev) => ({ ...prev, [cls.id]: 'marking' }));
    try {
      await api.post(`/api/attendance/classes/${cls.id}/self`);
      setAttendanceState((prev) => ({ ...prev, [cls.id]: 'marked' }));
      showToast('Attendance marked successfully!');
    } catch (err) {
      setAttendanceState((prev) => ({ ...prev, [cls.id]: null }));
      showToast(getErrorMessage(err, 'Failed to mark attendance'), 'error');
    }
  };

  if (loading) return <PageSkeleton />;

  if (error && !classes.length) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Failed to load classes</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">Retry</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Class Management</h1>
            <p className="text-sm text-gray-500 mt-1">Schedule and manage live classes</p>
          </div>
          {!isStudent && (
            <button onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors shadow-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Schedule Class
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Classes', value: totalClasses,     color: 'from-[#003479] to-[#0052b4]',  icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
            { label: 'Upcoming',      value: upcomingClasses,  color: 'from-[#20C997] to-emerald-600', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
            { label: 'Completed',     value: completedClasses, color: 'from-[#1B9AAA] to-[#005792]',  icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
            { label: 'Live Now',      value: liveClasses,      color: 'from-[#005792] to-[#003479]',  icon: 'M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z' },
          ].map((s) => (
            <div key={s.label} className={`bg-gradient-to-br ${s.color} rounded-2xl p-5 text-white shadow-lg`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">{(s.value ?? 0).toLocaleString()}</p>
                  <p className="text-sm opacity-90 mt-0.5">{s.label}</p>
                </div>
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={s.icon} />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search classes..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white min-w-[140px]">
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="live">Live</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white min-w-[140px]">
            <option value="">All Batches</option>
            {batches.map((b) => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
          </select>
          <button onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors text-sm text-gray-700 bg-white">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {sortDir === 'desc'
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />}
            </svg>
            {sortDir === 'desc' ? 'Newest' : 'Oldest'}
          </button>
        </div>

        {/* Class Grid */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">
                {search || statusFilter || batchFilter ? 'No classes match your filters' : 'No classes yet'}
              </p>
              {!search && !statusFilter && !batchFilter && !isStudent && (
                <button onClick={() => setShowForm(true)}
                  className="mt-5 px-5 py-2 bg-primary/10 text-primary text-sm font-medium rounded-xl hover:bg-primary/20 transition-colors">
                  Schedule Class
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((cls) => {
              const sc = getStatusConfig(cls.status);
              const isLive = cls.status === 'live';
              const isScheduled = cls.status === 'scheduled';
              const isFutureScheduled = isScheduled && new Date(cls.scheduled_at) > now;
              const isCancelled = cls.status === 'cancelled';
              const myAttendance = attendanceState[cls.id];

              return (
                <div key={cls.id}
                  className={`bg-white rounded-2xl border shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200 flex flex-col overflow-hidden group ${isLive ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-200'}`}>
                  {/* Status bar */}
                  <div className={`h-1.5 ${
                    isLive ? 'bg-gradient-to-r from-blue-500 to-cyan-400' :
                    isScheduled ? 'bg-gradient-to-r from-[#20C997] to-[#003479]' :
                    cls.status === 'completed' ? 'bg-gradient-to-r from-gray-300 to-gray-400' :
                    'bg-gradient-to-r from-red-400 to-red-500'
                  }`} />

                  <div className="p-5 flex flex-col flex-1">
                    {/* Title + status badge */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <h3 className="text-base font-bold text-gray-900 leading-snug group-hover:text-primary transition-colors line-clamp-2 flex-1">{cls.title}</h3>
                      <span className={`flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.text}`}>
                        {isLive
                          ? <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" /><span className={`relative inline-flex rounded-full h-2 w-2 ${sc.dot}`} /></span>
                          : <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />}
                        {sc.label}
                      </span>
                    </div>

                    {/* Batch & trainer */}
                    <div className="space-y-1.5 mb-3">
                      {[
                        { icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', text: cls.batch_name || 'No batch' },
                        { icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', text: cls.trainer_name || 'No trainer' },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                          </svg>
                          <span className="truncate">{item.text}</span>
                        </div>
                      ))}
                    </div>

                    {/* Date & duration */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatIST(cls.scheduled_at)}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatDuration(cls.duration_minutes)}
                      </span>
                      {isFutureScheduled && <CountdownBadge scheduledAt={cls.scheduled_at} />}
                      {/* Attendance count for trainers on live classes */}
                      {isLive && isAdminOrTrainer && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {attendanceCounts[cls.id] ?? 0} present
                        </span>
                      )}
                    </div>

                    <div className="flex-1" />

                    {/* Actions */}
                    <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
                      {/* Join Class button */}
                      {cls.meeting_link && (
                        isCancelled ? (
                          <button disabled className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-gray-400 bg-gray-100 rounded-xl cursor-not-allowed">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Class Cancelled
                          </button>
                        ) : (
                          <a href={cls.meeting_link} target="_blank" rel="noopener noreferrer"
                            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Join Class
                          </a>
                        )
                      )}

                      {/* Student: Mark Present button (only on live classes) */}
                      {isStudent && isLive && (
                        myAttendance === 'marked' ? (
                          <button disabled
                            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl cursor-not-allowed">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Marked Present
                          </button>
                        ) : (
                          <button
                            onClick={() => handleMarkPresent(cls)}
                            disabled={myAttendance === 'marking'}
                            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-60">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {myAttendance === 'marking' ? 'Marking...' : 'Mark Present'}
                          </button>
                        )
                      )}

                      {/* Trainer/Admin: Start Class / End Class */}
                      {isAdminOrTrainer && (
                        <div className="flex gap-2">
                          {isScheduled && (
                            <button onClick={() => handleStartClass(cls)}
                              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Start Class
                            </button>
                          )}
                          {isLive && (
                            <button onClick={() => handleEndClass(cls)}
                              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                              </svg>
                              End Class
                            </button>
                          )}
                          <button onClick={() => setEditClass(cls)}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                          <button onClick={() => setDeleteTarget(cls)}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <ClassFormModal cls={null} batches={batches} trainers={trainers}
          defaultTrainerId={user?.role === 'trainer' ? user.id : ''}
          onSave={handleCreate} onCancel={() => setShowForm(false)} loading={modalLoading} />
      )}
      {editClass && (
        <ClassFormModal cls={editClass} batches={batches} trainers={trainers}
          onSave={handleEdit} onCancel={() => setEditClass(null)} loading={modalLoading} />
      )}
      {deleteTarget && (
        <ConfirmModal
          title="Delete Class"
          message={`Are you sure you want to delete "${deleteTarget.title}"? This cannot be undone.`}
          confirmLabel="Delete" confirmColor="bg-red-600 hover:bg-red-700"
          onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} loading={modalLoading} />
      )}
    </Layout>
  );
};

export default ClassManagement;
